import { useState } from 'react';
import { sendFormEmail } from '../utils/emailService';

const hearAboutOptions = ['Select an option', 'Search Engine', 'LinkedIn', 'Instagram', 'Other'];

function ContactForm({ title = "Get In Touch", description, earlyAccess = false }) {
  const defaultDescription = (
    <>
      Ready to elevate your network management? Contact us today to learn how we{' '}
      <br className="hide-br" />
      can help streamline your operations and support your team.
    </>
  );

  const initialData = earlyAccess
    ? { firstName: '', lastName: '', email: '', company: '', jobTitle: '', phone: '', country: '', devices: '', hearAbout: '', query: '' }
    : { firstName: '', lastName: '', email: '', phone: '', message: '' };

  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const validateCompanyEmail = (email) => {
    const freeProviders = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    if (freeProviders.includes(domain)) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validate = () => {
    const newErrors = {};
    if (earlyAccess) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!validateCompanyEmail(formData.email)) {
        newErrors.email = 'Please use a company email address';
      }
      if (!formData.company.trim()) newErrors.company = 'Company name is required';
      if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.country.trim()) newErrors.country = 'Country is required';
      if (!formData.devices) {
        newErrors.devices = 'Please specify the number of devices';
      } else if (parseInt(formData.devices) < 1 || parseInt(formData.devices) > 20) {
        newErrors.devices = 'Device count must be between 1 and 20';
      }
    } else {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'devices') {
      const num = value === '' ? '' : Math.min(20, Math.max(0, parseInt(value) || 0));
      setFormData(prev => ({ ...prev, [name]: num.toString() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSending(true);
    setSendError('');
    try {
      await sendFormEmail(formData, earlyAccess);
      setSubmitted(true);
    } catch (err) {
      setSendError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-fluid what-we-offer-sec get-in-touch sec-padding" style={{ background: '#fff' }} id="demo-form">
      <div className="container">
        <h2 className="head-main after-border center">{title}</h2>
        <p>{description || defaultDescription}</p>
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="contact-form">
              {submitted ? (
                <div className="text-center" style={{ padding: '40px 20px' }}>
                  <div style={{ fontSize: '60px', color: '#10b981', marginBottom: '20px' }}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <h3 style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a2e', marginBottom: '12px' }}>
                    {earlyAccess ? 'Application Received' : 'Message Sent'}
                  </h3>
                  <p style={{ color: '#6b7280', fontSize: '15px' }}>
                    {earlyAccess
                      ? `Thank you, ${formData.firstName}! We've received your early access request. Our team will review your application and get back to you at ${formData.email} shortly.`
                      : 'Thank you for reaching out! Our team will get back to you shortly.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {earlyAccess ? (
                      <>
                        <div className="col-md-6">
                          <label htmlFor="cf-firstName" className="form-label">First Name <span style={{ color: '#ef4444' }}>*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                            id="cf-firstName"
                            name="firstName"
                            placeholder="John"
                            value={formData.firstName}
                            onChange={handleChange}
                          />
                          {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="cf-lastName" className="form-label">Last Name <span style={{ color: '#ef4444' }}>*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                            id="cf-lastName"
                            name="lastName"
                            placeholder="Smith"
                            value={formData.lastName}
                            onChange={handleChange}
                          />
                          {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="cf-email" className="form-label">Work Email <span style={{ color: '#ef4444' }}>*</span></label>
                          <input
                            type="email"
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            id="cf-email"
                            name="email"
                            placeholder="john@acmecorp.com"
                            value={formData.email}
                            onChange={handleChange}
                          />
                          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="cf-company" className="form-label">Company <span style={{ color: '#ef4444' }}>*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.company ? 'is-invalid' : ''}`}
                            id="cf-company"
                            name="company"
                            placeholder="Acme Corp"
                            value={formData.company}
                            onChange={handleChange}
                          />
                          {errors.company && <div className="invalid-feedback">{errors.company}</div>}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="cf-jobTitle" className="form-label">Job Title <span style={{ color: '#ef4444' }}>*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.jobTitle ? 'is-invalid' : ''}`}
                            id="cf-jobTitle"
                            name="jobTitle"
                            placeholder="Network Engineer"
                            value={formData.jobTitle}
                            onChange={handleChange}
                          />
                          {errors.jobTitle && <div className="invalid-feedback">{errors.jobTitle}</div>}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="cf-phone" className="form-label">Phone <span style={{ color: '#ef4444' }}>*</span></label>
                          <input
                            type="tel"
                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                            id="cf-phone"
                            name="phone"
                            placeholder="+1 (555) 123-4567"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                          {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="cf-country" className="form-label">Country <span style={{ color: '#ef4444' }}>*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                            id="cf-country"
                            name="country"
                            placeholder="United States"
                            value={formData.country}
                            onChange={handleChange}
                          />
                          {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="cf-hearAbout" className="form-label">How did you hear about us?</label>
                          <select
                            className="form-control"
                            id="cf-hearAbout"
                            name="hearAbout"
                            value={formData.hearAbout}
                            onChange={handleChange}
                          >
                            {hearAboutOptions.map((opt, i) => (
                              <option key={i} value={i === 0 ? '' : opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="cf-devices" className="form-label">Devices to Monitor <span style={{ color: '#ef4444' }}>*</span> <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '13px' }}>(max 20)</span></label>
                          <input
                            type="number"
                            className={`form-control ${errors.devices ? 'is-invalid' : ''}`}
                            id="cf-devices"
                            name="devices"
                            min="1"
                            max="20"
                            placeholder="e.g. 10"
                            value={formData.devices}
                            onChange={handleChange}
                          />
                          {errors.devices && <div className="invalid-feedback">{errors.devices}</div>}
                        </div>
                        <div className="col-md-6"></div>
                        <div className="col-12">
                          <label htmlFor="cf-query" className="form-label">Tell us about your use case <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '13px' }}>(optional)</span></label>
                          <textarea
                            className="form-control"
                            id="cf-query"
                            name="query"
                            rows="4"
                            placeholder="What challenges are you looking to solve with Netwatch?"
                            value={formData.query}
                            onChange={handleChange}
                          ></textarea>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-md-6">
                          <label htmlFor="your-name" className="form-label">First Name</label>
                          <input type="text" className="form-control" id="your-name" name="firstName" value={formData.firstName} onChange={handleChange} required />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="your-surname" className="form-label">Last Name</label>
                          <input type="text" className="form-control" id="your-surname" name="lastName" value={formData.lastName} onChange={handleChange} />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="your-email" className="form-label">Your Email</label>
                          <input type="email" className="form-control" id="your-email" name="email" value={formData.email} onChange={handleChange} required />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="your-subject" className="form-label">Phone Number</label>
                          <input type="text" className="form-control" id="your-subject" name="phone" value={formData.phone} onChange={handleChange} />
                        </div>
                        <div className="col-12">
                          <label htmlFor="your-message" className="form-label">Your Message</label>
                          <textarea className="form-control" id="your-message" name="message" rows="5" value={formData.message} onChange={handleChange} required></textarea>
                        </div>
                      </>
                    )}
                    {sendError && (
                      <div className="col-12">
                        <div style={{ color: '#ef4444', fontSize: '14px', padding: '8px 12px', background: '#fef2f2', borderRadius: '6px' }}>{sendError}</div>
                      </div>
                    )}
                    <div className="col-12">
                      <div className="row">
                        <div className="col-md-6">
                          <button type="submit" className="btn btn-send" disabled={sending}>
                            {sending ? 'Sending...' : (earlyAccess ? 'Submit Application' : 'Send')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactForm;
